'use client';

import { createContext, useContext } from 'react';

/**
 * Lightweight context that exposes "this workspace is being browsed
 * by an unauthenticated visitor" to anywhere inside `WorkspaceShell`
 * without props-drilling.
 *
 * The shell mounts the provider unconditionally; consumers read
 * `anonReadOnly` to decide whether to substitute auth-only chrome
 * with a sign-up CTA or swap the `?dialog=` target to `/signup`.
 * A true-anon edit no longer opens a signup dialog — the first write
 * mints a GUEST (see `lib/api/fetch-with-guest-mint.ts`), after which
 * the visitor has a session and `anonReadOnly` is false.
 *
 * `sourceWorkspaceId` is the public/unlisted workspace the anon
 * visitor is browsing — the workspace cloned into a guest-owned copy
 * when they materialise (via the splash "Open app", the create FAB,
 * or the first-write safety net).
 */
export type WorkspaceAnonState = {
  anonReadOnly: boolean;
  sourceWorkspaceId: string | null;
};

const Ctx = createContext<WorkspaceAnonState>({
  anonReadOnly: false,
  sourceWorkspaceId: null,
});

export function WorkspaceAnonProvider({
  anonReadOnly,
  sourceWorkspaceId,
  children,
}: WorkspaceAnonState & { children: React.ReactNode }) {
  return (
    <Ctx.Provider value={{ anonReadOnly, sourceWorkspaceId }}>{children}</Ctx.Provider>
  );
}

export function useWorkspaceAnon() {
  return useContext(Ctx);
}
